/**
 * Structured logging system with environment-aware output
 * 
 * @module logger
 * @description
 * Production-safe logging system that handles sensitive data filtering,
 * environment detection, and audit trails. Logs are automatically filtered
 * in production to prevent sensitive data leaks.
 * 
 * @example
 * ```ts
 * import { logger } from '@/utils/logger';
 * 
 * // Standard logging
 * logger.error('Failed to load user', error);
 * logger.warn('Rate limit approaching');
 * logger.info('User logged in', { userId: '123' });
 * 
 * // Sensitive data (won't log in production)
 * logger.error('Auth failed', error, true);
 * 
 * // Audit logging (always persists)
 * logger.audit('User accessed admin panel', { userId, action });
 * ```
 */
export const logger = {
  /**
   * Log error messages with optional error object
   * In production, only logs non-sensitive errors
   * 
   * @param message - Error message
   * @param error - Error object or additional data
   * @param sensitiveData - If true, won't log in production
   */
  error: (message: string, error?: Error | unknown, sensitiveData?: boolean) => {
    const isDevelopment = import.meta.env?.DEV ?? false;
    if (isDevelopment) {
      console.error(message, error);
    } else {
      // Em produção, log apenas erros não sensíveis
      if (!sensitiveData) {
        // TODO: Integrar com Sentry ou similar
        console.error(message);
      }
    }
  },
  
  /**
   * Log warning messages
   * 
   * @param message - Warning message
   * @param data - Additional data to log
   * @param sensitiveData - If true, won't log in production
   */
  warn: (message: string, data?: unknown, sensitiveData?: boolean) => {
    const isDevelopment = import.meta.env?.DEV ?? false;
    if (isDevelopment) {
      console.warn(message, data);
    } else if (!sensitiveData) {
      // Log warnings não sensíveis em produção
      console.warn(message);
    }
  },
  
  /**
   * Log informational messages
   * Only appears in development mode
   * 
   * @param message - Info message
   * @param data - Additional data to log
   * @param sensitiveData - Reserved for future use
   */
  info: (message: string, data?: unknown, sensitiveData?: boolean) => {
    const isDevelopment = import.meta.env?.DEV ?? false;
    if (isDevelopment) {
      console.log(message, data);
    }
    // Info logs não aparecem em produção por segurança
  },
  
  /**
   * Log debug messages
   * Only appears in development mode, never in production
   * 
   * @param message - Debug message
   * @param data - Additional debug data
   */
  debug: (message: string, data?: unknown) => {
    const isDevelopment = import.meta.env?.DEV ?? false;
    if (isDevelopment) {
      console.debug(message, data);
    }
    // Debug logs nunca aparecem em produção
  },
  
  /**
   * Alias para migração gradual de console.log existentes
   * Só loga em desenvolvimento
   * 
   * @param message - Log message
   * @param data - Additional data
   */
  log: (message: string, data?: unknown) => {
    const isDevelopment = import.meta.env?.DEV ?? false;
    if (isDevelopment) {
      console.log(message, data);
    }
  },
  
  /**
   * Special audit logger that always persists events
   * Stores last 1000 events in localStorage for security review
   * 
   * @param message - Audit event message
   * @param data - Event metadata (user, action, resource, etc.)
   * 
   * @example
   * ```ts
   * logger.audit('User deleted record', {
   *   userId: '123',
   *   action: 'DELETE',
   *   resource: 'users',
   *   resourceId: '456'
   * });
   * ```
   */
  audit: (message: string, data?: Record<string, any>) => {
    const auditEvent = {
      timestamp: new Date().toISOString(),
      message,
      data: data || {}
    };
    
    // Store audit logs locally for security review
    try {
      const auditLogs = JSON.parse(localStorage.getItem('audit_logs') || '[]');
      auditLogs.push(auditEvent);
      localStorage.setItem('audit_logs', JSON.stringify(auditLogs.slice(-1000))); // Keep last 1000
    } catch (e) {
      // Silently fail if localStorage is not available
    }
    
    const isDevelopment = import.meta.env?.DEV ?? false;
    if (isDevelopment) {
      console.log('AUDIT:', auditEvent);
    }
  },
  
  /**
   * Logger for Supabase Edge Functions
   * Always logs with ISO timestamps for production debugging
   */
  edgeFunction: {
    /**
     * Log info messages from edge functions
     * @param message - Log message
     * @param data - Additional data
     */
    info: (message: string, data?: any) => {
      // Edge functions sempre logam info para debugging
      console.log(`[${new Date().toISOString()}] ${message}`, data || '');
    },
    
    /**
     * Log errors from edge functions
     * @param message - Error message
     * @param error - Error object
     * @param sensitiveData - If true, only logs message without error details
     */
    error: (message: string, error?: any, sensitiveData?: boolean) => {
      if (sensitiveData) {
        console.error(`[${new Date().toISOString()}] ${message}`);
      } else {
        console.error(`[${new Date().toISOString()}] ${message}`, error || '');
      }
    },
    
    /**
     * Log warnings from edge functions
     * @param message - Warning message
     * @param data - Additional data
     */
    warn: (message: string, data?: any) => {
      console.warn(`[${new Date().toISOString()}] ${message}`, data || '');
    }
  }
};